import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Receipt as ReceiptIcon, FileText, Percent, Euro, Tag, Check, Plus, X, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const PAYMENT_METHODS = [
    { id: "contanti", label: "Contanti" },
    { id: "carta", label: "Carta" },
    { id: "bancomat", label: "Bancomat" },
    { id: "satispay", label: "Satispay" },
    { id: "bonifico", label: "Bonifico" },
    { id: "ticket", label: "Ticket" },
    { id: "misto", label: "Misto" },
];

// Calcola subtotale, sconto, totale di un singolo item considerando sconti per item E extras
const itemNet = (it) => {
    const extrasSum = (it.extras || []).reduce((s, e) => s + (parseFloat(e.price) || 0), 0);
    const gross = (it.price + extrasSum) * it.quantity;
    let net = gross;
    if (it.discount_pct) net = net * (1 - it.discount_pct / 100);
    if (it.discount_amt) net = Math.max(0, net - it.discount_amt);
    return { gross, net };
};

const CassaDetail = () => {
    const { tableId } = useParams();
    const [searchParams] = useSearchParams();
    const requestedOrderId = searchParams.get("order");
    const navigate = useNavigate();
    const [table, setTable] = useState(null);
    const [order, setOrder] = useState(null);
    const [discountMode, setDiscountMode] = useState("pct"); // pct, amt
    const [discountPct, setDiscountPct] = useState(0);
    const [discountAmtInput, setDiscountAmtInput] = useState(0);
    const [method, setMethod] = useState("contanti");
    const [docType, setDocType] = useState("scontrino");
    const [invoiceData, setInvoiceData] = useState({
        name: "", fiscal_code: "", vat: "", sdi: "", pec: "", address: "",
    });
    const [splitMode, setSplitMode] = useState("unico"); // unico, romana, dividi
    const [splitCount, setSplitCount] = useState(2);
    const [selectedIds, setSelectedIds] = useState([]); // per dividi conto
    const [itemDiscFor, setItemDiscFor] = useState(null);
    const [itemDiscMode, setItemDiscMode] = useState("pct");
    const [itemDiscValue, setItemDiscValue] = useState(0);
    const [showFreeItem, setShowFreeItem] = useState(false);
    const [freeItemForm, setFreeItemForm] = useState({ name: "", price: "", quantity: 1, vat_rate: 10, course: "primo", department: "cucina", notes: "" });
    const [voidFor, setVoidFor] = useState(null);
    const [voidReason, setVoidReason] = useState("");
    // Ticket payment (misto)
    const [showTicketModal, setShowTicketModal] = useState(false);
    const [ticketAmount, setTicketAmount] = useState("");
    const [ticketRestMethod, setTicketRestMethod] = useState("contanti");
    // Convenzione: override prezzo singolo piatto
    const [convFor, setConvFor] = useState(null);
    const [convPrice, setConvPrice] = useState("");
    const [convReason, setConvReason] = useState("");
    const [convBusy, setConvBusy] = useState(false);
    const [voidBusy, setVoidBusy] = useState(false);
    const { user } = useAuth() || {};
    const canVoid = user?.role === "admin" || user?.role === "cashier";

    const VOID_PRESETS = ["Errore battitura", "Cliente rifiutato", "Cortesia / Omaggio", "Cambio piatto", "Difetto cucina"];

    const openVoid = (item) => { setVoidFor(item); setVoidReason(""); };

    const confirmVoid = async () => {
        const reason = (voidReason || "").trim();
        if (!reason) { toast.error("Specifica il motivo della cancellazione"); return; }
        setVoidBusy(true);
        try {
            await api.delete(`/orders/${order.id}/items/${voidFor.id}`, { params: { reason } });
            toast.success(`"${voidFor.name}" rimosso dal conto`);
            setVoidFor(null);
            setVoidReason("");
            await load();
        } catch (e) { toast.error(formatError(e)); }
        setVoidBusy(false);
    };

    const load = async () => {
        const tablesRes = await api.get("/tables");
        const t = tablesRes.data.find((x) => x.id === tableId);
        setTable(t);
        const ordersRes = await api.get("/orders");
        // Se è richiesto un ordine specifico (es. da BIS awaiting_payment), usa quello.
        // Altrimenti usa l'ordine attivo del tavolo (status != chiuso, non awaiting_payment).
        let oRaw;
        if (requestedOrderId) {
            oRaw = ordersRes.data.find((x) => x.id === requestedOrderId);
        } else {
            oRaw = ordersRes.data.find((x) => x.table_id === tableId && x.status !== "chiuso" && x.awaiting_payment !== true);
        }
        if (oRaw) {
            try {
                const { data: oFull } = await api.get(`/orders/${oRaw.id}`);
                setOrder(oFull);
                setDiscountPct(oFull.discount_pct || 0);
                setDiscountAmtInput(oFull.discount_amt || 0);
                if (oFull.discount_amt && !oFull.discount_pct) setDiscountMode("amt");
            } catch {
                setOrder(oRaw);
            }
        } else {
            setOrder(null);
        }
    };
    useEffect(() => { load(); }, [tableId, requestedOrderId]);

    // Item NON pagati
    const unpaidItems = useMemo(() => (order?.items || []).filter((it) => !it.paid_at), [order]);
    const paidItems = useMemo(() => (order?.items || []).filter((it) => it.paid_at), [order]);

    // Subtotale rimanente (tutti gli item non pagati al netto degli sconti per item)
    const subtotalRemaining = useMemo(
        () => unpaidItems.reduce((s, it) => s + itemNet(it).net, 0),
        [unpaidItems]
    );

    // Sconto globale applicato sopra subtotale rimanente
    const globalDiscountAmt = useMemo(() => {
        if (discountMode === "pct") return subtotalRemaining * ((discountPct || 0) / 100);
        return Math.min(subtotalRemaining, discountAmtInput || 0);
    }, [subtotalRemaining, discountMode, discountPct, discountAmtInput]);

    // Coperto: somma adulti+bambini dal menu attivo (solo se nessun item è stato già pagato → evita doppio addebito)
    const copertoBreakdown = useMemo(() => {
        if (!order) return { adults: 0, children: 0, total: 0 };
        if (paidItems.length > 0) return { adults: 0, children: 0, total: 0 };
        const adultsTot = order.coperto_enabled ? (order.coperto_adults_total || 0) : 0;
        const childrenTot = order.coperto_child_enabled ? (order.coperto_children_total || 0) : 0;
        return {
            adults: Math.round(adultsTot * 100) / 100,
            children: Math.round(childrenTot * 100) / 100,
            total: Math.round((adultsTot + childrenTot) * 100) / 100,
        };
    }, [order, paidItems.length]);
    const copertoTotal = copertoBreakdown.total;

    const total = Math.max(0, subtotalRemaining - globalDiscountAmt + copertoTotal);

    // Romana
    const splitAmount = splitMode === "romana" ? total / splitCount : total;

    // Dividi conto: somma item selezionati (al netto degli sconti per-item)
    const selectedGross = useMemo(() => {
        if (splitMode !== "dividi") return 0;
        return unpaidItems
            .filter((it) => selectedIds.includes(it.id))
            .reduce((s, it) => s + itemNet(it).net, 0);
    }, [unpaidItems, selectedIds, splitMode]);

    // Quota pro-rata dello sconto globale che ricade sulla selezione
    const selectedDiscountShare = useMemo(() => {
        if (splitMode !== "dividi" || subtotalRemaining <= 0 || globalDiscountAmt <= 0) return 0;
        return globalDiscountAmt * (selectedGross / subtotalRemaining);
    }, [splitMode, subtotalRemaining, globalDiscountAmt, selectedGross]);

    // Totale finale della selezione (con sconto globale pro-rata applicato)
    const selectedTotal = Math.max(0, selectedGross - selectedDiscountShare);

    const toggleSelect = (id) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };
    const selectAll = () => setSelectedIds(unpaidItems.map((it) => it.id));
    const selectNone = () => setSelectedIds([]);

    const applyGlobalDiscount = async () => {
        try {
            const body = discountMode === "pct"
                ? { discount_pct: discountPct, discount_amt: 0 }
                : { discount_pct: 0, discount_amt: discountAmtInput };
            await api.patch(`/orders/${order.id}/discount`, body);
            toast.success("Sconto applicato");
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const openItemDiscount = (it) => {
        setItemDiscFor(it);
        setItemDiscMode(it.discount_amt ? "amt" : "pct");
        setItemDiscValue(it.discount_amt || it.discount_pct || 0);
    };

    const applyItemDiscount = async () => {
        if (!itemDiscFor) return;
        try {
            const body = itemDiscMode === "pct"
                ? { discount_pct: itemDiscValue, discount_amt: 0 }
                : { discount_pct: 0, discount_amt: itemDiscValue };
            await api.patch(`/orders/${order.id}/items/${itemDiscFor.id}/discount`, body);
            toast.success(`Sconto applicato su "${itemDiscFor.name}"`);
            setItemDiscFor(null);
            await load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const openConvenzione = (it) => {
        setConvFor(it);
        setConvPrice(String(it.price));
        setConvReason("Convenzione");
    };

    const applyConvenzione = async () => {
        if (!convFor) return;
        const p = parseFloat(String(convPrice).replace(",", "."));
        if (!isFinite(p) || p < 0) { toast.error("Prezzo non valido"); return; }
        if (p > 9999) { toast.error("Prezzo troppo elevato"); return; }
        const reason = (convReason || "Convenzione").trim();
        setConvBusy(true);
        try {
            await api.patch(`/orders/${order.id}/items/${convFor.id}/price`, {
                price: p,
                reason,
            });
            toast.success(`"${convFor.name}": nuovo prezzo € ${p.toFixed(2)}`);
            setConvFor(null);
            setConvPrice("");
            setConvReason("");
            await load();
        } catch (e) { toast.error(formatError(e)); }
        setConvBusy(false);
    };

    const openTicketPayment = () => {
        setTicketAmount("");
        setTicketRestMethod("contanti");
        setShowTicketModal(true);
    };

    const confirmTicketPayment = async () => {
        const ticket = parseFloat(String(ticketAmount).replace(",", "."));
        if (!isFinite(ticket) || ticket <= 0) {
            toast.error("Importo ticket non valido");
            return;
        }
        const totalDue = splitMode === "dividi" ? selectedTotal : total;
        if (ticket > totalDue + 0.01) {
            toast.error(`Il ticket (€ ${ticket.toFixed(2)}) supera l'importo dovuto (€ ${totalDue.toFixed(2)})`);
            return;
        }
        const rest = Math.max(0, Math.round((totalDue - ticket) * 100) / 100);
        const splits = [
            { method: "ticket", amount: Math.round(ticket * 100) / 100 },
        ];
        if (rest > 0) {
            splits.push({ method: ticketRestMethod, amount: rest });
        }
        try {
            if (splitMode === "dividi") {
                if (selectedIds.length === 0) { toast.error("Seleziona almeno un piatto"); return; }
                const payload = {
                    order_id: order.id,
                    item_ids: selectedIds,
                    amount: totalDue,
                    method: "misto",
                    document_type: docType,
                    invoice_data: docType === "fattura" ? invoiceData : null,
                    discount_amt: Math.round(selectedDiscountShare * 100) / 100,
                    discount_pct: selectedGross > 0 && selectedDiscountShare > 0
                        ? Math.round((selectedDiscountShare / selectedGross) * 10000) / 100
                        : 0,
                    subtotal_gross: Math.round(selectedGross * 100) / 100,
                    splits,
                };
                const { data } = await api.post("/payments/partial", payload);
                toast.success(`Pagamento misto · Ticket € ${ticket.toFixed(2)}${rest > 0 ? ` + ${ticketRestMethod} € ${rest.toFixed(2)}` : ""}`);
                setShowTicketModal(false);
                if (data.all_paid) navigate("/cassa");
                else { setSelectedIds([]); await load(); }
                return;
            }
            const payload = {
                order_id: order.id,
                amount: totalDue,
                method: "misto",
                document_type: docType,
                invoice_data: docType === "fattura" ? invoiceData : null,
                splits,
            };
            const { data } = await api.post("/payments", payload);
            toast.success(`Pagamento misto · Ticket € ${ticket.toFixed(2)}${rest > 0 ? ` + ${ticketRestMethod} € ${rest.toFixed(2)}` : ""} · ${docType === "fattura" ? "Fattura" : "Scontrino"} ${data.document.number}`);
            setShowTicketModal(false);
            navigate("/cassa");
        } catch (e) { toast.error(formatError(e)); }
    };

    const completePayment = async () => {
        try {
            if (splitMode === "dividi") {
                if (selectedIds.length === 0) {
                    toast.error("Seleziona almeno un piatto");
                    return;
                }
                const payload = {
                    order_id: order.id,
                    item_ids: selectedIds,
                    amount: selectedTotal,
                    method,
                    document_type: docType,
                    invoice_data: docType === "fattura" ? invoiceData : null,
                    // Quota pro-rata dello sconto globale applicata a questa selezione
                    discount_amt: Math.round(selectedDiscountShare * 100) / 100,
                    discount_pct: selectedGross > 0 && selectedDiscountShare > 0
                        ? Math.round((selectedDiscountShare / selectedGross) * 10000) / 100
                        : 0,
                    subtotal_gross: Math.round(selectedGross * 100) / 100,
                };
                const { data } = await api.post("/payments/partial", payload);
                toast.success(`${docType === "fattura" ? "Fattura" : "Scontrino"} ${data.document.number} emesso · € ${selectedTotal.toFixed(2)}`);
                if (data.all_paid) {
                    navigate("/cassa");
                } else {
                    setSelectedIds([]);
                    await load();
                    toast.info(`Restano ${data.remaining_items.length} piatti da pagare`);
                }
                return;
            }

            // Unico o romana → pagamento totale
            const payload = {
                order_id: order.id,
                amount: total,
                method,
                document_type: docType,
                invoice_data: docType === "fattura" ? invoiceData : null,
                splits: splitMode === "romana"
                    ? Array.from({ length: splitCount }, () => ({ amount: splitAmount, method }))
                    : [],
            };
            const { data } = await api.post("/payments", payload);
            toast.success(`${docType === "fattura" ? "Fattura" : "Scontrino"} ${data.document.number} emesso`);
            navigate("/cassa");
        } catch (e) { toast.error(formatError(e)); }
    };

    if (!table || !order) return <div className="text-elegant">Caricamento...</div>;

    const addFreeItem = async () => {
        const name = (freeItemForm.name || "").trim();
        const price = parseFloat(freeItemForm.price);
        if (!name) { toast.error("Inserisci il nome dell'articolo"); return; }
        if (!price || price <= 0) { toast.error("Inserisci un prezzo valido"); return; }
        try {
            await api.post(`/orders/${order.id}/items/free`, {
                name,
                price,
                quantity: parseInt(freeItemForm.quantity, 10) || 1,
                vat_rate: parseFloat(freeItemForm.vat_rate),
                course: freeItemForm.course,
                department: freeItemForm.department,
                notes: freeItemForm.notes || "",
            });
            toast.success(`"${name}" aggiunto al conto`);
            setShowFreeItem(false);
            setFreeItemForm({ name: "", price: "", quantity: 1, vat_rate: 10, course: "primo", department: "cucina", notes: "" });
            await load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const payAmount = splitMode === "dividi" ? selectedTotal : total;
    const payDisabled = payAmount <= 0;

    return (
        <div className="space-y-6" data-testid="cassa-detail-page">
            <button onClick={() => navigate("/cassa")} className="flex items-center gap-2 text-elegant hover:text-brand">
                <ArrowLeft className="w-4 h-4" /> Torna alla cassa
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Bill */}
                <div className="lg:col-span-2 bg-surface border border-[#1f2c44] rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] text-brand mb-1">Conto</div>
                            <div className="font-display text-3xl font-bold">Tavolo {table.number}</div>
                            <div className="text-xs text-elegant mt-1">Cameriere: {order.waiter_name}</div>
                        </div>
                        {splitMode === "dividi" && (
                            <div className="flex gap-2">
                                <button onClick={selectAll} className="h-9 px-3 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md text-xs">Tutti</button>
                                <button onClick={selectNone} className="h-9 px-3 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md text-xs">Nessuno</button>
                            </div>
                        )}
                        <button onClick={() => setShowFreeItem(true)} data-testid="add-free-item-button"
                            className="h-9 px-3 border border-brand bg-brand/10 hover:bg-brand/20 text-brand rounded-md text-xs uppercase tracking-wider flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Articolo libero
                        </button>
                    </div>

                    {paidItems.length > 0 && (
                        <div className="mb-3 bg-brand-soft border border-brand/30 rounded-md p-2">
                            <div className="text-[10px] uppercase tracking-wider text-brand font-bold mb-1 flex items-center gap-1">
                                <Check className="w-3 h-3" /> Già pagati ({paidItems.length})
                            </div>
                            {paidItems.map((it) => (
                                <div key={it.id} className="text-xs flex justify-between text-elegant line-through">
                                    <span>{it.name} ×{it.quantity}</span>
                                    <span>€ {itemNet(it).net.toFixed(2)}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2" data-testid="bill-items">
                        {unpaidItems.map((it) => {
                            const { gross, net } = itemNet(it);
                            const hasDisc = (it.discount_pct || 0) > 0 || (it.discount_amt || 0) > 0;
                            const isSelected = selectedIds.includes(it.id);
                            return (
                                <div key={it.id} className={`flex justify-between gap-2 border-b border-[#1f2c44] pb-2 ${
                                    splitMode === "dividi" && isSelected ? "bg-brand-soft -mx-2 px-2 rounded" : ""
                                }`}>
                                    {splitMode === "dividi" && (
                                        <button onClick={() => toggleSelect(it.id)} data-testid={`select-item-${it.id}`}
                                            className={`mt-1 w-6 h-6 rounded border flex-shrink-0 flex items-center justify-center ${
                                                isSelected ? "bg-brand border-brand" : "border-[#1f2c44]"
                                            }`}>
                                            {isSelected && <Check className="w-4 h-4 text-deep" />}
                                        </button>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium">{it.name} <span className="text-elegant text-xs">×{it.quantity}</span></div>
                                        {it.notes && <div className="text-[11px] italic text-amber-400">{it.notes}</div>}
                                        {it.extras?.length > 0 && (
                                            <div className="mt-1 space-y-0.5" data-testid={`cassa-extras-${it.id}`}>
                                                {it.extras.map((ex) => (
                                                    <div key={ex.id} className="text-[11px] text-cyan-300 flex items-center gap-1">
                                                        <span className="font-bold">+</span>
                                                        <span>{ex.label}</span>
                                                        {ex.price > 0 && <span className="text-cyan-400 font-mono">· +€ {parseFloat(ex.price).toFixed(2)}</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {hasDisc && (
                                            <div className="text-[10px] text-brand mt-1 flex items-center gap-1">
                                                <Tag className="w-3 h-3" />
                                                {it.discount_pct ? `-${it.discount_pct}%` : `-€${it.discount_amt?.toFixed(2)}`}
                                            </div>
                                        )}
                                        <div className="flex gap-2 mt-1 flex-wrap">
                                            <button onClick={() => openItemDiscount(it)} data-testid={`item-disc-${it.id}`}
                                                className="text-[10px] text-elegant hover:text-brand uppercase tracking-wider">
                                                {hasDisc ? "Modifica sconto" : "Sconto piatto"}
                                            </button>
                                            <button onClick={() => openConvenzione(it)} data-testid={`item-conv-${it.id}`}
                                                title="Sostituisci il prezzo (convenzione cliente)"
                                                className="text-[10px] text-amber-300 hover:text-amber-100 uppercase tracking-wider font-bold flex items-center gap-1">
                                                <Tag className="w-3 h-3" /> Convenzione
                                            </button>
                                            {canVoid && (
                                                <button onClick={() => openVoid(it)} data-testid={`cassa-void-${it.id}`}
                                                    title="Elimina dal conto (motivo obbligatorio)"
                                                    className="text-[10px] text-red-400 hover:text-red-200 uppercase tracking-wider font-bold flex items-center gap-1">
                                                    <Trash2 className="w-3 h-3" /> Annulla
                                                </button>
                                            )}
                                        </div>
                                        {it.original_price && it.original_price !== it.price && (
                                            <div className="text-[10px] text-amber-300 mt-1 flex items-center gap-1" data-testid={`item-conv-applied-${it.id}`}>
                                                <Tag className="w-3 h-3" /> Convenzione: € {it.original_price.toFixed(2)} → € {it.price.toFixed(2)}
                                                {it.price_override_reason && it.price_override_reason !== "Convenzione" && <span className="opacity-70">· {it.price_override_reason}</span>}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-right whitespace-nowrap">
                                        {hasDisc && <div className="text-[10px] text-elegant line-through">€ {gross.toFixed(2)}</div>}
                                        <div className="font-display font-bold">€ {net.toFixed(2)}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-6 space-y-3 text-sm">
                        <div className="flex justify-between text-elegant">
                            <span>Subtotale {paidItems.length > 0 ? "(non pagato)" : ""}</span>
                            <span>€ {subtotalRemaining.toFixed(2)}</span>
                        </div>

                        {/* Sconto globale */}
                        <div className="bg-deep border border-[#1f2c44] rounded-md p-3 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-elegant text-xs uppercase tracking-wider">Sconto totale</span>
                                <div className="flex border border-[#1f2c44] rounded overflow-hidden">
                                    <button onClick={() => setDiscountMode("pct")}
                                        className={`px-3 h-8 text-xs flex items-center gap-1 ${discountMode === "pct" ? "bg-brand text-deep" : "text-elegant"}`}
                                        data-testid="disc-mode-pct">
                                        <Percent className="w-3 h-3" /> %
                                    </button>
                                    <button onClick={() => setDiscountMode("amt")}
                                        className={`px-3 h-8 text-xs flex items-center gap-1 ${discountMode === "amt" ? "bg-brand text-deep" : "text-elegant"}`}
                                        data-testid="disc-mode-amt">
                                        <Euro className="w-3 h-3" /> Netto
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {discountMode === "pct" ? (
                                    <input type="number" min="0" max="100" step="0.5" value={discountPct}
                                        onChange={(e) => setDiscountPct(parseFloat(e.target.value) || 0)}
                                        onBlur={applyGlobalDiscount} data-testid="discount-pct-input"
                                        className="flex-1 h-10 px-2 bg-surface-elevated border border-[#1f2c44] rounded text-right" />
                                ) : (
                                    <input type="number" min="0" step="0.5" value={discountAmtInput}
                                        onChange={(e) => setDiscountAmtInput(parseFloat(e.target.value) || 0)}
                                        onBlur={applyGlobalDiscount} data-testid="discount-amt-input"
                                        className="flex-1 h-10 px-2 bg-surface-elevated border border-[#1f2c44] rounded text-right" />
                                )}
                                <span className="text-elegant whitespace-nowrap">- € {globalDiscountAmt.toFixed(2)}</span>
                            </div>
                        </div>

                        {order.cover_charge_disabled && (
                            <div className="bg-amber-900/20 border border-amber-500/50 rounded-md p-3 flex items-center justify-between" data-testid="bill-coperto-disabled">
                                <div className="flex items-center gap-2 text-amber-200 text-sm">
                                    <span className="font-bold">⚠</span>
                                    <span>Coperto NON addebitato (deciso dal cameriere)</span>
                                </div>
                                <span className="font-display font-bold text-amber-300 text-xs uppercase tracking-wider">€ 0,00</span>
                            </div>
                        )}

                        {copertoBreakdown.adults > 0 && (
                            <div className="bg-deep border border-brand/30 rounded-md p-3 flex items-center justify-between" data-testid="bill-coperto">
                                <div className="flex items-center gap-2 text-elegant text-sm">
                                    <span>Coperto × {order.covers}</span>
                                    <span className="text-[10px] opacity-60">(€ {(order.coperto_unit || 0).toFixed(2)} a persona)</span>
                                </div>
                                <span className="font-display font-bold text-brand">+ € {copertoBreakdown.adults.toFixed(2)}</span>
                            </div>
                        )}
                        {copertoBreakdown.children > 0 && (
                            <div className="bg-deep border border-amber-500/30 rounded-md p-3 flex items-center justify-between" data-testid="bill-coperto-children">
                                <div className="flex items-center gap-2 text-amber-300 text-sm">
                                    <span>👶 Coperto bambini × {order.covers_children}</span>
                                    <span className="text-[10px] opacity-60">(€ {(order.coperto_child_unit || 0).toFixed(2)} a bambino)</span>
                                </div>
                                <span className="font-display font-bold text-amber-300">+ € {copertoBreakdown.children.toFixed(2)}</span>
                            </div>
                        )}

                        <div className="flex justify-between font-display text-2xl font-bold pt-3 border-t border-[#333]">
                            <span>Totale da pagare</span><span className="text-brand" data-testid="bill-total">€ {total.toFixed(2)}</span>
                        </div>

                        {splitMode === "dividi" && (
                            <div className="pt-2 border-t border-brand/30 space-y-1.5">
                                <div className="flex justify-between text-xs text-elegant">
                                    <span>Selezione (lordo)</span>
                                    <span>€ {selectedGross.toFixed(2)}</span>
                                </div>
                                {selectedDiscountShare > 0 && (
                                    <div className="flex justify-between text-xs text-amber-300">
                                        <span>Sconto pro-rata su selezione</span>
                                        <span>- € {selectedDiscountShare.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between font-display text-xl font-bold">
                                    <span className="text-brand">Da incassare ora</span>
                                    <span className="text-brand" data-testid="bill-selected-total">€ {selectedTotal.toFixed(2)}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Payment */}
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 space-y-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-brand mb-2">Modalità conto</div>
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                { id: "unico", label: "Unico" },
                                { id: "romana", label: "Romana" },
                                { id: "dividi", label: "Dividi" },
                            ].map((s) => (
                                <button key={s.id} onClick={() => { setSplitMode(s.id); setSelectedIds([]); }} data-testid={`split-${s.id}`}
                                    className={`h-12 rounded-md border text-xs uppercase tracking-wider ${
                                        splitMode === s.id ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44] text-elegant"
                                    }`}>{s.label}</button>
                            ))}
                        </div>
                        {splitMode === "romana" && (
                            <div className="flex items-center gap-2 mt-3">
                                <span className="text-xs text-elegant">Persone:</span>
                                <input type="number" min="2" value={splitCount} onChange={(e) => setSplitCount(parseInt(e.target.value) || 2)}
                                    className="w-20 h-10 px-2 bg-surface-elevated border border-[#1f2c44] rounded" data-testid="split-count" />
                                <span className="text-sm">€ {splitAmount.toFixed(2)} a persona</span>
                            </div>
                        )}
                        {splitMode === "dividi" && (
                            <div className="text-xs text-elegant mt-3">
                                Spunta i piatti da pagare ora. Gli altri restano a conto aperto.
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-brand mb-2">Metodo Pagamento</div>
                        <div className="grid grid-cols-2 gap-2">
                            {PAYMENT_METHODS.map((m) => (
                                <button key={m.id} onClick={() => setMethod(m.id)} data-testid={`method-${m.id}`}
                                    className={`h-12 rounded-md border text-sm ${
                                        method === m.id ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44] text-elegant"
                                    }`}>{m.label}</button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-brand mb-2">Documento</div>
                        <div className="grid grid-cols-2 gap-2">
                            <button onClick={() => setDocType("scontrino")} data-testid="doc-scontrino"
                                className={`h-12 rounded-md border text-sm flex items-center justify-center gap-2 ${
                                    docType === "scontrino" ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44] text-elegant"
                                }`}><ReceiptIcon className="w-4 h-4" /> Scontrino</button>
                            <button onClick={() => setDocType("fattura")} data-testid="doc-fattura"
                                className={`h-12 rounded-md border text-sm flex items-center justify-center gap-2 ${
                                    docType === "fattura" ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44] text-elegant"
                                }`}><FileText className="w-4 h-4" /> Fattura</button>
                        </div>
                    </div>

                    {docType === "fattura" && (
                        <div className="space-y-2">
                            <input className="w-full h-10 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-sm"
                                placeholder="Ragione sociale / Nome" value={invoiceData.name}
                                onChange={(e) => setInvoiceData({ ...invoiceData, name: e.target.value })} data-testid="inv-name" />
                            <div className="grid grid-cols-2 gap-2">
                                <input className="h-10 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-sm" placeholder="Codice Fiscale"
                                    value={invoiceData.fiscal_code} onChange={(e) => setInvoiceData({ ...invoiceData, fiscal_code: e.target.value })} />
                                <input className="h-10 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-sm" placeholder="P. IVA"
                                    value={invoiceData.vat} onChange={(e) => setInvoiceData({ ...invoiceData, vat: e.target.value })} />
                                <input className="h-10 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-sm" placeholder="Codice SDI"
                                    value={invoiceData.sdi} onChange={(e) => setInvoiceData({ ...invoiceData, sdi: e.target.value })} />
                                <input className="h-10 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-sm" placeholder="PEC"
                                    value={invoiceData.pec} onChange={(e) => setInvoiceData({ ...invoiceData, pec: e.target.value })} />
                            </div>
                            <input className="w-full h-10 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-sm"
                                placeholder="Indirizzo" value={invoiceData.address}
                                onChange={(e) => setInvoiceData({ ...invoiceData, address: e.target.value })} />
                        </div>
                    )}

                    <button onClick={completePayment} disabled={payDisabled} data-testid="complete-payment-button"
                        className="w-full h-16 bg-brand text-deep font-bold uppercase tracking-wider text-lg rounded-md disabled:opacity-40">
                        Incassa € {payAmount.toFixed(2)}
                    </button>

                    <button onClick={openTicketPayment} disabled={payDisabled} data-testid="ticket-payment-button"
                        className="w-full h-12 bg-amber-600 hover:bg-amber-500 text-white font-bold uppercase tracking-wider text-sm rounded-md disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
                        🎫 Pagamento con ticket
                    </button>
                </div>
            </div>

            {/* Modal sconto per item */}
            {itemDiscFor && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setItemDiscFor(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-surface border border-[#1f2c44] rounded-2xl p-6 max-w-sm w-full">
                        <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Sconto piatto</div>
                        <div className="font-display text-2xl font-bold mb-1">{itemDiscFor.name}</div>
                        <div className="text-xs text-elegant mb-4">Prezzo: € {(itemDiscFor.price * itemDiscFor.quantity).toFixed(2)}</div>

                        <div className="flex border border-[#1f2c44] rounded overflow-hidden mb-3">
                            <button onClick={() => setItemDiscMode("pct")}
                                className={`flex-1 h-11 text-sm flex items-center justify-center gap-1 ${itemDiscMode === "pct" ? "bg-brand text-deep" : "text-elegant"}`}
                                data-testid="item-disc-mode-pct">
                                <Percent className="w-4 h-4" /> Percentuale
                            </button>
                            <button onClick={() => setItemDiscMode("amt")}
                                className={`flex-1 h-11 text-sm flex items-center justify-center gap-1 ${itemDiscMode === "amt" ? "bg-brand text-deep" : "text-elegant"}`}
                                data-testid="item-disc-mode-amt">
                                <Euro className="w-4 h-4" /> Netto
                            </button>
                        </div>

                        <input type="number" min="0" step="0.5" value={itemDiscValue}
                            onChange={(e) => setItemDiscValue(parseFloat(e.target.value) || 0)}
                            data-testid="item-disc-value"
                            className="w-full h-14 px-4 text-2xl text-center font-bold bg-deep border-2 border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" />
                        <div className="text-xs text-elegant text-center mt-2">
                            {itemDiscMode === "pct" ? `${itemDiscValue}% di sconto` : `€ ${itemDiscValue.toFixed(2)} di sconto`}
                        </div>

                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setItemDiscFor(null)} className="flex-1 h-12 border border-[#1f2c44] rounded-md text-elegant">Annulla</button>
                            <button onClick={applyItemDiscount} data-testid="apply-item-disc"
                                className="flex-1 h-12 bg-brand text-deep font-bold rounded-md">Applica</button>
                        </div>
                    </div>
                </div>
            )}

            {showFreeItem && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-end lg:items-center justify-center p-2 lg:p-4" onClick={() => setShowFreeItem(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-surface border border-[#1f2c44] rounded-2xl p-6 max-w-md w-full" data-testid="free-item-modal">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <div className="text-xs uppercase tracking-[0.3em] text-brand mb-1">Articolo libero</div>
                                <div className="font-display text-2xl font-bold">Tavolo {table.number}</div>
                                <div className="text-xs text-elegant mt-1">Aggiungi un articolo non in menu</div>
                            </div>
                            <button onClick={() => setShowFreeItem(false)}><X className="text-elegant" /></button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Nome articolo *</label>
                                <input type="text" value={freeItemForm.name}
                                    onChange={(e) => setFreeItemForm({ ...freeItemForm, name: e.target.value })}
                                    placeholder="Es. Caffè extra, Pane supplementare, ..."
                                    data-testid="free-item-name-input"
                                    className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Prezzo (€) *</label>
                                    <input type="number" step="0.50" min="0" value={freeItemForm.price}
                                        onChange={(e) => setFreeItemForm({ ...freeItemForm, price: e.target.value })}
                                        placeholder="0.00"
                                        data-testid="free-item-price-input"
                                        className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Quantità</label>
                                    <input type="number" min="1" value={freeItemForm.quantity}
                                        onChange={(e) => setFreeItemForm({ ...freeItemForm, quantity: e.target.value })}
                                        data-testid="free-item-qty-input"
                                        className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">IVA</label>
                                    <select value={freeItemForm.vat_rate}
                                        onChange={(e) => setFreeItemForm({ ...freeItemForm, vat_rate: parseFloat(e.target.value) })}
                                        data-testid="free-item-vat-select"
                                        className="w-full h-12 px-2 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none">
                                        <option value="4">4%</option>
                                        <option value="10">10%</option>
                                        <option value="22">22%</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Reparto</label>
                                    <select value={freeItemForm.department}
                                        onChange={(e) => setFreeItemForm({ ...freeItemForm, department: e.target.value })}
                                        className="w-full h-12 px-2 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none">
                                        <option value="cucina">Cucina</option>
                                        <option value="pizzeria">Pizzeria</option>
                                        <option value="bar">Bar</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Portata</label>
                                    <select value={freeItemForm.course}
                                        onChange={(e) => setFreeItemForm({ ...freeItemForm, course: e.target.value })}
                                        className="w-full h-12 px-2 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none">
                                        <option value="antipasto">Antipasto</option>
                                        <option value="primo">Primo</option>
                                        <option value="secondo">Secondo</option>
                                        <option value="contorno">Contorno</option>
                                        <option value="pizza">Pizza</option>
                                        <option value="dolce">Dolce</option>
                                        <option value="bevanda">Bevanda</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Note (opzionale)</label>
                                <input type="text" value={freeItemForm.notes}
                                    onChange={(e) => setFreeItemForm({ ...freeItemForm, notes: e.target.value })}
                                    placeholder="Es. senza zucchero"
                                    className="w-full h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none text-sm" />
                            </div>
                        </div>

                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setShowFreeItem(false)} className="flex-1 h-12 border border-[#1f2c44] rounded-md text-elegant">Annulla</button>
                            <button onClick={addFreeItem} data-testid="confirm-free-item-button"
                                className="flex-1 h-12 bg-brand text-deep font-bold rounded-md flex items-center justify-center gap-2">
                                <Plus className="w-4 h-4" /> Aggiungi al conto
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale void item */}
            {voidFor && (
                <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4" data-testid="cassa-void-modal">
                    <div className="bg-surface border-2 border-red-500 rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-red-900/40 border border-red-500 flex items-center justify-center">
                                <Trash2 className="w-6 h-6 text-red-300" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-wider text-red-300">Annulla dal conto</div>
                                <div className="font-display text-xl font-bold truncate">{voidFor.name}</div>
                                <div className="text-xs text-elegant">×{voidFor.quantity} · € {(voidFor.price * voidFor.quantity).toFixed(2)} · {voidFor.status}</div>
                            </div>
                        </div>

                        <div className="bg-amber-900/20 border border-amber-500/50 rounded-md p-2 mb-4 text-xs text-amber-200 flex items-start gap-2">
                            <span className="font-bold">⚠</span>
                            <span>L'azione viene registrata nel log per audit. La cucina riceverà notifica.</span>
                        </div>

                        <div className="mb-3">
                            <div className="text-[10px] uppercase tracking-wider text-elegant mb-2 font-bold">Motivo (obbligatorio)</div>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                                {VOID_PRESETS.map((p) => (
                                    <button key={p} onClick={() => setVoidReason(p)}
                                        className={`h-10 px-2 text-[11px] uppercase tracking-wider rounded border font-bold transition-colors ${
                                            voidReason === p
                                                ? "border-red-400 bg-red-900/40 text-red-100"
                                                : "border-[#1f2c44] text-elegant hover:border-red-500/60"
                                        }`}>
                                        {p}
                                    </button>
                                ))}
                            </div>
                            <input value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
                                placeholder="…oppure scrivi il motivo" maxLength={200}
                                data-testid="cassa-void-reason-input"
                                className="w-full h-11 px-3 bg-deep border border-[#1f2c44] rounded text-warm focus:border-red-500 focus:outline-none text-sm" />
                        </div>

                        <div className="flex gap-2">
                            <button onClick={() => { setVoidFor(null); setVoidReason(""); }}
                                disabled={voidBusy} data-testid="cassa-void-cancel"
                                className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant disabled:opacity-50">Mantieni</button>
                            <button onClick={confirmVoid} disabled={voidBusy || !voidReason.trim()}
                                data-testid="cassa-void-confirm"
                                className="flex-1 h-12 bg-red-700 hover:bg-red-600 disabled:bg-red-900/30 disabled:cursor-not-allowed text-white font-bold rounded uppercase tracking-wider text-xs flex items-center justify-center gap-2">
                                <Trash2 className="w-4 h-4" /> {voidBusy ? "Elimino..." : "Elimina"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Convenzione: override prezzo singolo piatto */}
            {convFor && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !convBusy && setConvFor(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-surface border-2 border-amber-500/60 rounded-2xl p-6 max-w-sm w-full" data-testid="conv-modal">
                        <div className="flex items-center gap-2 mb-2">
                            <Tag className="w-5 h-5 text-amber-400" />
                            <div className="text-xs uppercase tracking-[0.3em] text-amber-300">Convenzione</div>
                        </div>
                        <div className="font-display text-2xl font-bold mb-1">{convFor.name}</div>
                        <div className="text-xs text-elegant mb-4">Prezzo attuale: <strong className="text-warm">€ {convFor.price.toFixed(2)}</strong>{convFor.original_price && convFor.original_price !== convFor.price && <> · Originale: € {convFor.original_price.toFixed(2)}</>}</div>

                        <div className="mb-3">
                            <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">Nuovo prezzo € *</label>
                            <input type="number" min="0" step="0.01" value={convPrice}
                                onChange={(e) => setConvPrice(e.target.value)}
                                data-testid="conv-price-input"
                                placeholder="0.00"
                                autoFocus
                                className="w-full h-12 px-3 bg-deep border-2 border-amber-500/40 rounded text-right font-display text-2xl font-bold text-warm focus:border-amber-500 focus:outline-none" />
                        </div>

                        <div className="mb-4">
                            <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">Motivo (audit)</label>
                            <input type="text" value={convReason}
                                onChange={(e) => setConvReason(e.target.value)}
                                placeholder="Es. Convenzione AZ S.p.A., cliente abituale…"
                                maxLength={200}
                                data-testid="conv-reason-input"
                                className="w-full h-10 px-3 bg-deep border border-[#1f2c44] rounded text-warm focus:border-amber-500 focus:outline-none text-sm" />
                        </div>

                        <div className="flex gap-2">
                            <button onClick={() => setConvFor(null)} disabled={convBusy} data-testid="conv-cancel"
                                className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant disabled:opacity-50 uppercase tracking-wider text-xs font-bold">
                                Annulla
                            </button>
                            <button onClick={applyConvenzione} disabled={convBusy || !convPrice}
                                data-testid="conv-confirm"
                                className="flex-1 h-12 bg-amber-500 hover:bg-amber-400 disabled:bg-amber-900/30 disabled:cursor-not-allowed text-deep font-bold rounded uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition-colors">
                                {convBusy ? "Applico..." : "Applica"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Pagamento Ticket (misto) */}
            {showTicketModal && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowTicketModal(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-surface border-2 border-amber-500/60 rounded-2xl p-6 max-w-md w-full" data-testid="ticket-modal">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="text-2xl">🎫</span>
                            <div>
                                <div className="text-xs uppercase tracking-[0.3em] text-amber-300">Pagamento con ticket</div>
                                <div className="font-display text-xl font-bold">Importo da incassare: € {payAmount.toFixed(2)}</div>
                            </div>
                        </div>
                        <div className="text-xs text-elegant mb-4">Inserisci l'importo del ticket. Se la cifra è inferiore al totale, il resto verrà pagato col metodo selezionato.</div>

                        <div className="mb-4">
                            <label className="text-[10px] uppercase tracking-wider text-amber-300 block mb-1">Importo ticket € *</label>
                            <input type="number" min="0" step="0.50" value={ticketAmount}
                                onChange={(e) => setTicketAmount(e.target.value)}
                                placeholder="es. 8.00"
                                autoFocus
                                data-testid="ticket-amount-input"
                                className="w-full h-14 px-3 bg-deep border-2 border-amber-500/40 rounded text-right font-display text-3xl font-bold text-amber-200 focus:border-amber-500 focus:outline-none" />
                            <div className="flex gap-1 mt-2 flex-wrap">
                                {[5, 7, 8, 10, 12, 15].map((v) => (
                                    <button key={v} onClick={() => setTicketAmount(String(v))}
                                        data-testid={`ticket-quick-${v}`}
                                        className="h-9 px-3 rounded border border-amber-500/40 text-amber-300 text-xs uppercase tracking-wider hover:bg-amber-500/10">
                                        € {v}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Anteprima residuo */}
                        {(() => {
                            const ticket = parseFloat(String(ticketAmount || "0").replace(",", "."));
                            const valid = isFinite(ticket) && ticket > 0;
                            const rest = valid ? Math.max(0, Math.round((payAmount - ticket) * 100) / 100) : payAmount;
                            return (
                                <div className="bg-deep border border-[#1f2c44] rounded p-3 mb-4 space-y-1.5 text-sm" data-testid="ticket-preview">
                                    <div className="flex justify-between text-amber-300">
                                        <span>Ticket</span>
                                        <span className="font-display font-bold">€ {(valid ? ticket : 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-warm pt-1 border-t border-[#1f2c44]">
                                        <span>Residuo da pagare</span>
                                        <span className="font-display font-bold text-brand">€ {rest.toFixed(2)}</span>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Metodo per il residuo */}
                        {(() => {
                            const ticket = parseFloat(String(ticketAmount || "0").replace(",", "."));
                            const valid = isFinite(ticket) && ticket > 0;
                            const rest = valid ? Math.max(0, Math.round((payAmount - ticket) * 100) / 100) : payAmount;
                            if (rest <= 0) return (
                                <div className="bg-emerald-900/20 border border-emerald-500/40 rounded p-3 mb-4 text-xs text-emerald-200">
                                    ✓ Il ticket copre l'intero importo. Non serve un secondo metodo.
                                </div>
                            );
                            return (
                                <div className="mb-4">
                                    <div className="text-[10px] uppercase tracking-wider text-brand mb-2">Pagamento residuo</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        {[
                                            { id: "contanti", label: "Contanti" },
                                            { id: "carta", label: "Carta" },
                                            { id: "bancomat", label: "Bancomat" },
                                            { id: "satispay", label: "Satispay" },
                                        ].map((m) => (
                                            <button key={m.id} onClick={() => setTicketRestMethod(m.id)}
                                                data-testid={`ticket-rest-${m.id}`}
                                                className={`h-12 rounded-md border text-sm ${
                                                    ticketRestMethod === m.id ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44] text-elegant hover:border-brand/60"
                                                }`}>
                                                {m.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}

                        <div className="flex gap-2">
                            <button onClick={() => setShowTicketModal(false)} data-testid="ticket-cancel"
                                className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant uppercase tracking-wider text-xs font-bold">
                                Annulla
                            </button>
                            <button onClick={confirmTicketPayment} disabled={!ticketAmount}
                                data-testid="ticket-confirm"
                                className="flex-[1.5] h-12 bg-amber-500 hover:bg-amber-400 disabled:bg-amber-900/30 disabled:cursor-not-allowed text-deep font-bold rounded uppercase tracking-wider text-xs transition-colors">
                                Conferma e incassa
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CassaDetail;
